/**
 * ProjectStepper — Full-page project creation wizard.
 *
 * Supports three paths:
 *   - Guided: multi-step walkthrough for new users
 *   - Quick: condensed form, creates project immediately
 *   - Template: (future) start from a pre-built template
 *
 * Route: /projects/new
 *
 * Option B persistence: creates the project record on final step (Review)
 * via POST /api/Projects, then navigates to the new project detail page.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { createProject, updateProject } from '../../api/projects';
import { createProjectAclEntry } from '../../api/security';
import { invalidateAndRefetch, QUERY_KEYS } from '../../api/queryCache';
import { uploadFile, fileUrl } from '../../api/fileStorage';
import { compressImage } from '../../lib/compressImage';
import { getSystemSetting, SETTING_KEYS } from '../../api/systemSettings';
import {
  getActiveCategoryOptions,
  projectStatusOptions,
  contentStatusOptions,
  localizedProjectCategory,
  localizedProjectStatus,
  localizedContentStatus,
  getTypesForCategory,
  getSubtypesForType,
  localizedProjectType,
  localizedProjectSubtype,
} from '../../Data/ProjectConfig/projectStandardOptions';
import LocationPickerMap from './LocationPickerMap';
import CommunityPicker from './CommunityPicker';
import styles from './ProjectStepper.module.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type StepperPath = 'guided' | 'quick' | 'template';

interface ProjectDraft {
  name: string;
  description: string;
  heroFile: File | null;
  heroPreviewUrl: string | null;
  category: string;
  projectType: string;
  projectSubtype: string;
  projectStatus: string;
  contentStatus: string;
  locationLat: string;
  locationLon: string;
  primaryLocationEntityGuid: string;
  communityId: string;
  communityDisplay: string;
  startDate: string;
  endDate: string;
  reportingCadence: string;
}

const EMPTY_DRAFT: ProjectDraft = {
  name: '',
  description: '',
  heroFile: null,
  heroPreviewUrl: null,
  category: '',
  projectType: '',
  projectSubtype: '',
  projectStatus: 'Proposed',
  contentStatus: 'Drafting',
  locationLat: '',
  locationLon: '',
  primaryLocationEntityGuid: '',
  communityId: '',
  communityDisplay: '',
  startDate: '',
  endDate: '',
  reportingCadence: '',
};

/* ------------------------------------------------------------------ */
/*  Step definitions                                                    */
/* ------------------------------------------------------------------ */

interface StepDef {
  key: string;
  required: boolean;
}

const GUIDED_STEPS: StepDef[] = [
  { key: 'name',      required: true },
  { key: 'category',  required: false },
  { key: 'location',  required: false },
  { key: 'community', required: false },
  { key: 'timeline',  required: false },
  { key: 'kpi',       required: false },
  { key: 'preflight', required: false },
  { key: 'review',    required: true },
];

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ProjectStepperProps {
  currentUserGuid: string;
  onCreated?: () => void;
  /** Community entities from App.tsx apiCommunityEntities — needed for the community picker */
  communities?: any[];
  /** Community entity relationships (parent→child) for path display in the community picker */
  communityRelationships?: any[];
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ProjectStepper({ currentUserGuid, onCreated, communities = [], communityRelationships = [] }: ProjectStepperProps) {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();

  // ── Path & step state ─────────────────────────────────────
  const [path, setPath] = useState<StepperPath | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [skippedSteps, setSkippedSteps] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState<ProjectDraft>({ ...EMPTY_DRAFT });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const heroInputRef = useRef<HTMLInputElement>(null);

  // ── Configurable intro video URL ───────────────────────────
  const FALLBACK_VIDEO_URL = 'https://www.youtube-nocookie.com/embed/mXwr77kG5uw?rel=0&modestbranding=1';
  const [videoUrl, setVideoUrl] = useState<string>(FALLBACK_VIDEO_URL);

  useEffect(() => {
    let cancelled = false;
    getSystemSetting(SETTING_KEYS.STEPPER_VIDEO_URL).then((val) => {
      if (!cancelled && val) setVideoUrl(val);
    }).catch(() => { /* use fallback */ });
    return () => { cancelled = true; };
  }, []);

  const steps = GUIDED_STEPS;
  const currentStep = steps[stepIndex];

  // ── Draft updater ─────────────────────────────────────────
  const patch = useCallback((updates: Partial<ProjectDraft>) => {
    setDraft((prev) => ({ ...prev, ...updates }));
  }, []);

  // ── Navigation ────────────────────────────────────────────
  const goNext = useCallback(() => {
    setStepIndex((i) => Math.min(steps.length - 1, i + 1));
  }, [steps.length]);

  const goBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const goToStep = useCallback((idx: number) => {
    setStepIndex(idx);
  }, []);

  const skipStep = useCallback(() => {
    if (currentStep) {
      setSkippedSteps((prev) => new Set(prev).add(currentStep.key));
    }
    goNext();
  }, [currentStep, goNext]);

  // ── Validation ────────────────────────────────────────────
  const canProceed = useMemo(() => {
    if (!currentStep) return false;
    switch (currentStep.key) {
      case 'name': return draft.name.trim().length > 0;
      case 'review': return draft.name.trim().length > 0;
      default: return true;
    }
  }, [currentStep, draft.name]);

  // ── Hero photo handler ────────────────────────────────────
  const handleHeroFile = useCallback(async (file: File) => {
    const url = URL.createObjectURL(file);
    patch({ heroFile: file, heroPreviewUrl: url });
  }, [patch]);

  // ── Create project ────────────────────────────────────────
  const handleCreate = useCallback(async () => {
    if (!draft.name.trim()) return;
    setCreating(true);
    setError(null);

    try {
      // 1. Create the project record
      const result = await createProject({
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        projectCategory: draft.category || undefined,
        projectStatus: draft.projectStatus || 'Proposed',
        contentStatus: draft.contentStatus || 'Drafting',
        locationLat: draft.locationLat || undefined,
        locationLon: draft.locationLon || undefined,
        primaryLocationEntityGuid: draft.primaryLocationEntityGuid || undefined,
        communityId: draft.communityId || undefined,
        startDate: draft.startDate || undefined,
        finishDate: draft.endDate || undefined,
        reportingCadence: draft.reportingCadence || undefined,
      });

      const projectId = result.id;

      // 2. Upload hero image if provided
      if (draft.heroFile && projectId) {
        try {
          const { blob } = await compressImage(draft.heroFile, {
            maxDimension: 1920,
            quality: 0.85,
            maxBytes: 500_000,
          });
          const ts = Date.now();
          const slug = Math.random().toString(36).slice(2, 6);
          const fileName = `hero-${ts}-${slug}.jpg`;
          const uploadResult = await uploadFile(blob, 'heroes', projectId, fileName);
          await updateProject(projectId, {
            heroImageUrl: fileUrl(uploadResult.key),
          });
        } catch (imgErr) {
          console.error('[ProjectStepper] Hero upload failed (project still created):', imgErr);
        }
      }

      // 3. Grant owner_admin ACL to the creating user
      try {
        await createProjectAclEntry({
          projectGuid: projectId,
          userGuid: currentUserGuid,
          role: 'owner_admin',
        });
      } catch (aclErr) {
        console.warn('[ProjectStepper] ACL grant failed (project still created):', aclErr);
      }

      // 4. Invalidate project + security caches so the Data array and ACL pick up the new project
      invalidateAndRefetch(QUERY_KEYS.projects);
      invalidateAndRefetch(QUERY_KEYS.security);

      // 5. Notify parent and navigate to new project
      onCreated?.();
      navigate(`/projects/${projectId}`);
    } catch (err: any) {
      console.error('[ProjectStepper] Create failed:', err);
      setError(err.message || 'Failed to create project');
      setCreating(false);
    }
  }, [draft, navigate, onCreated, currentUserGuid]);

  // ── Quick create ──────────────────────────────────────────
  const handleQuickCreate = useCallback(async () => {
    if (!draft.name.trim()) return;
    setCreating(true);
    setError(null);

    try {
      const result = await createProject({
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        projectCategory: draft.category || undefined,
        projectStatus: 'Proposed',
        contentStatus: 'Drafting',
        communityId: draft.communityId || undefined,
      });

      // Grant owner_admin ACL to the creating user
      try {
        await createProjectAclEntry({
          projectGuid: result.id,
          userGuid: currentUserGuid,
          role: 'owner_admin',
        });
      } catch (aclErr) {
        console.warn('[ProjectStepper] ACL grant failed (project still created):', aclErr);
      }

      invalidateAndRefetch(QUERY_KEYS.projects);
      invalidateAndRefetch(QUERY_KEYS.security);
      onCreated?.();
      navigate(`/projects/${result.id}`);
    } catch (err: any) {
      console.error('[ProjectStepper] Quick create failed:', err);
      setError(err.message || 'Failed to create project');
      setCreating(false);
    }
  }, [draft, navigate, onCreated, currentUserGuid]);

  // ── Path selection screen ─────────────────────────────────
  if (path === null) {
    return (
      <div className={styles.shell}>
        <div className={styles.topBar}>
          <button className={styles.backBtn} onClick={() => navigate(-1)} type="button">
            ← {t('stepper.back')}
          </button>
          <div className={styles.topTitle}>{t('stepper.title')}</div>
        </div>

        <div className={styles.content}>
          <div className={styles.stepContainer}>
            <h2 className={styles.stepTitle}>{t('stepper.pathTitle')}</h2>
            <p className={styles.stepHint}>{t('stepper.pathHint')}</p>

            {/* Intro video — hidden when URL is blank */}
            {videoUrl && (
              <div className={styles.videoSlot}>
                <iframe
                  src={videoUrl}
                  title={t('stepper.videoTitle')}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className={styles.videoIframe}
                />
              </div>
            )}

            <div className={styles.pathGrid}>
              {/* Guided */}
              <button className={styles.pathCard} onClick={() => setPath('guided')} type="button">
                <span className={styles.pathIcon}>🧭</span>
                <span className={styles.pathLabel}>{t('stepper.pathGuided')}</span>
                <span className={styles.pathDesc}>{t('stepper.pathGuidedDesc')}</span>
              </button>

              {/* Quick */}
              <button className={styles.pathCard} onClick={() => setPath('quick')} type="button">
                <span className={styles.pathIcon}>⚡</span>
                <span className={styles.pathLabel}>{t('stepper.pathQuick')}</span>
                <span className={styles.pathDesc}>{t('stepper.pathQuickDesc')}</span>
              </button>

              {/* Template (disabled) */}
              <button className={styles.pathCard} data-disabled="true" type="button">
                <span className={styles.pathIcon}>📋</span>
                <span className={styles.pathLabel}>{t('stepper.pathTemplate')}</span>
                <span className={styles.pathDesc}>{t('stepper.pathTemplateDesc')}</span>
                <span className={styles.pathBadge}>{t('stepper.comingSoon')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Quick setup ───────────────────────────────────────────
  if (path === 'quick') {
    return (
      <div className={styles.shell}>
        <div className={styles.topBar}>
          <button className={styles.backBtn} onClick={() => setPath(null)} type="button">
            ← {t('stepper.back')}
          </button>
          <div className={styles.topTitle}>{t('stepper.quickTitle')}</div>
        </div>

        <div className={styles.content}>
          <div className={styles.stepContainer}>
            <h2 className={styles.stepTitle}>{t('stepper.quickHeading')}</h2>
            <p className={styles.stepHint}>{t('stepper.quickHint')}</p>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{t('stepper.nameLabel')} *</label>
              <input
                className={styles.formInput}
                value={draft.name}
                onChange={(e) => patch({ name: e.target.value })}
                placeholder={t('stepper.namePlaceholder')}
                maxLength={200}
                autoFocus
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{t('stepper.descLabel')}</label>
              <textarea
                className={styles.formTextarea}
                value={draft.description}
                onChange={(e) => patch({ description: e.target.value })}
                placeholder={t('stepper.descPlaceholder')}
                rows={3}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{t('stepper.categoryLabel')}</label>
              <select
                className={styles.formSelect}
                value={draft.category}
                onChange={(e) => patch({ category: e.target.value })}
              >
                <option value="">{t('stepper.categoryPlaceholder')}</option>
                {getActiveCategoryOptions().map((c) => (
                  <option key={c} value={c}>{localizedProjectCategory(c)}</option>
                ))}
              </select>
            </div>

            {error && <div style={{ color: 'var(--accent-danger)', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            <div className={styles.footer} style={{ border: 'none', padding: '16px 0 0' }}>
              <button className={styles.btnSecondary} onClick={() => setPath(null)} type="button">
                {t('stepper.cancel')}
              </button>
              <button
                className={styles.btnPrimary}
                onClick={handleQuickCreate}
                disabled={!draft.name.trim() || creating}
                type="button"
              >
                {creating ? t('stepper.creating') : t('stepper.createProject')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Guided setup ──────────────────────────────────────────
  const renderStep = () => {
    if (!currentStep) return null;

    switch (currentStep.key) {
      /* ── Step 1: Name + Description + Hero ── */
      case 'name':
        return (
          <>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{t('stepper.nameLabel')} *</label>
              <input
                className={styles.formInput}
                value={draft.name}
                onChange={(e) => patch({ name: e.target.value })}
                placeholder={t('stepper.namePlaceholder')}
                maxLength={200}
                autoFocus
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{t('stepper.descLabel')}</label>
              <textarea
                className={styles.formTextarea}
                value={draft.description}
                onChange={(e) => patch({ description: e.target.value })}
                placeholder={t('stepper.descPlaceholder')}
                rows={3}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{t('stepper.heroLabel')}</label>
              <div
                className={styles.heroUploadZone}
                onClick={() => heroInputRef.current?.click()}
              >
                {draft.heroPreviewUrl ? (
                  <>
                    <img className={styles.heroPreview} src={draft.heroPreviewUrl} alt="" />
                    <button
                      className={styles.heroRemoveBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (draft.heroPreviewUrl) URL.revokeObjectURL(draft.heroPreviewUrl);
                        patch({ heroFile: null, heroPreviewUrl: null });
                      }}
                      type="button"
                    >
                      ×
                    </button>
                  </>
                ) : (
                  <div className={styles.heroUploadPlaceholder}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    {t('stepper.heroPlaceholder')}
                  </div>
                )}
              </div>
              <input
                ref={heroInputRef}
                type="file"
                accept=".jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleHeroFile(file);
                  e.target.value = '';
                }}
              />
              <div className={styles.formHint}>{t('stepper.heroHint')}</div>
            </div>
          </>
        );

      /* ── Step 2: Category & Type ── */
      case 'category':
        return (
          <>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{t('stepper.categoryLabel')}</label>
              <select
                className={styles.formSelect}
                value={draft.category}
                onChange={(e) => patch({ category: e.target.value, projectType: '', projectSubtype: '' })}
              >
                <option value="">{t('stepper.categoryPlaceholder')}</option>
                {getActiveCategoryOptions().map((c) => (
                  <option key={c} value={c}>{localizedProjectCategory(c)}</option>
                ))}
              </select>
              <div className={styles.formHint}>{t('stepper.categoryHint')}</div>
            </div>

            {/* Type — cascades from category */}
            {draft.category && getTypesForCategory(draft.category).length > 0 && (
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{t('stepper.typeLabel')}</label>
                <select
                  className={styles.formSelect}
                  value={draft.projectType}
                  onChange={(e) => patch({ projectType: e.target.value, projectSubtype: '' })}
                >
                  <option value="">{t('stepper.typePlaceholder')}</option>
                  {getTypesForCategory(draft.category).map((tv) => (
                    <option key={tv} value={tv}>{localizedProjectType(tv)}</option>
                  ))}
                </select>
                <div className={styles.formHint}>{t('stepper.typeHint')}</div>
              </div>
            )}

            {/* Subtype — cascades from type */}
            {draft.category && draft.projectType && getSubtypesForType(draft.category, draft.projectType).length > 0 && (
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{t('stepper.subtypeLabel')}</label>
                <select
                  className={styles.formSelect}
                  value={draft.projectSubtype}
                  onChange={(e) => patch({ projectSubtype: e.target.value })}
                >
                  <option value="">{t('stepper.subtypePlaceholder')}</option>
                  {getSubtypesForType(draft.category, draft.projectType).map((sv) => (
                    <option key={sv} value={sv}>{localizedProjectSubtype(sv)}</option>
                  ))}
                </select>
              </div>
            )}

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{t('stepper.statusLabel')}</label>
              <select
                className={styles.formSelect}
                value={draft.projectStatus}
                onChange={(e) => patch({ projectStatus: e.target.value })}
              >
                {projectStatusOptions.map((s) => (
                  <option key={s} value={s}>{localizedProjectStatus(s)}</option>
                ))}
              </select>
            </div>
          </>
        );

      /* ── Step 3: Location ── */
      case 'location':
        return (
          <>
            {/* Interactive map — click or drag to place pin */}
            <LocationPickerMap
              lat={draft.locationLat}
              lon={draft.locationLon}
              onChange={(lat, lon) => patch({ locationLat: lat, locationLon: lon })}
            />
            <div className={styles.formHint} style={{ marginTop: -12, marginBottom: 16 }}>
              {t('stepper.mapClickHint')}
            </div>

            {/* Manual lat/lon fallback */}
            <div style={{ display: 'flex', gap: 12 }}>
              <div className={styles.formGroup} style={{ flex: 1 }}>
                <label className={styles.formLabel}>{t('stepper.latLabel')}</label>
                <input
                  className={styles.formInput}
                  type="text"
                  value={draft.locationLat}
                  onChange={(e) => patch({ locationLat: e.target.value })}
                  placeholder={t('stepper.latPlaceholder')}
                />
              </div>
              <div className={styles.formGroup} style={{ flex: 1 }}>
                <label className={styles.formLabel}>{t('stepper.lonLabel')}</label>
                <input
                  className={styles.formInput}
                  type="text"
                  value={draft.locationLon}
                  onChange={(e) => patch({ locationLon: e.target.value })}
                  placeholder={t('stepper.lonPlaceholder')}
                />
              </div>
            </div>
          </>
        );

      /* ── Step 4: Community ── */
      case 'community':
        return (
          <>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{t('stepper.communityLabel')}</label>
              <CommunityPicker
                currentUserGuid={currentUserGuid}
                value={draft.communityId}
                displayValue={draft.communityDisplay}
                onChange={(guid, display) => patch({ communityId: guid, communityDisplay: display })}
                communities={communities}
                relationships={communityRelationships}
              />
              <div className={styles.formHint}>{t('stepper.communityHint')}</div>
            </div>
          </>
        );

      /* ── Step 5: Timeline ── */
      case 'timeline':
        return (
          <>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{t('stepper.startDateLabel')}</label>
              <input
                className={styles.formInput}
                type="date"
                value={draft.startDate}
                onChange={(e) => patch({ startDate: e.target.value })}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{t('stepper.endDateLabel')}</label>
              <input
                className={styles.formInput}
                type="date"
                value={draft.endDate}
                onChange={(e) => patch({ endDate: e.target.value })}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>{t('stepper.cadenceLabel')}</label>
              <select
                className={styles.formSelect}
                value={draft.reportingCadence}
                onChange={(e) => patch({ reportingCadence: e.target.value })}
              >
                <option value="">{t('stepper.cadencePlaceholder')}</option>
                <option value="daily">{t('stepper.cadenceDaily')}</option>
                <option value="weekly">{t('stepper.cadenceWeekly')}</option>
                <option value="bi-weekly">{t('stepper.cadenceBiWeekly')}</option>
                <option value="monthly">{t('stepper.cadenceMonthly')}</option>
                <option value="quarterly">{t('stepper.cadenceQuarterly')}</option>
                <option value="annual">{t('stepper.cadenceAnnual')}</option>
              </select>
              <div className={styles.formHint}>{t('stepper.cadenceHint')}</div>
            </div>
          </>
        );

      /* ── Step 6: KPI Profile (placeholder) ── */
      case 'kpi':
        return (
          <div className={styles.placeholder}>
            <div className={styles.placeholderIcon}>📊</div>
            <div className={styles.placeholderTitle}>{t('stepper.kpiPlaceholderTitle')}</div>
            <div className={styles.placeholderDesc}>{t('stepper.kpiPlaceholderDesc')}</div>
          </div>
        );

      /* ── Step 7: Pre-Flight (placeholder) ── */
      case 'preflight':
        return (
          <div className={styles.placeholder}>
            <div className={styles.placeholderIcon}>✅</div>
            <div className={styles.placeholderTitle}>{t('stepper.preflightPlaceholderTitle')}</div>
            <div className={styles.placeholderDesc}>{t('stepper.preflightPlaceholderDesc')}</div>
          </div>
        );

      /* ── Step 8: Review & Create ── */
      case 'review':
        return (
          <>
            {/* Name */}
            <div className={styles.reviewSection}>
              <div className={styles.reviewLabel}>
                {t('stepper.nameLabel')}
                <button className={styles.reviewEditBtn} onClick={() => goToStep(0)} type="button">{t('stepper.edit')}</button>
              </div>
              <div className={styles.reviewValue}>{draft.name || '—'}</div>
              {draft.description && (
                <div className={styles.reviewValue} style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {draft.description}
                </div>
              )}
            </div>

            {/* Category */}
            <div className={styles.reviewSection}>
              <div className={styles.reviewLabel}>
                {t('stepper.categoryLabel')}
                <button className={styles.reviewEditBtn} onClick={() => goToStep(1)} type="button">{t('stepper.edit')}</button>
              </div>
              {skippedSteps.has('category') ? (
                <div className={styles.reviewSkipped}>{t('stepper.skippedNote')}</div>
              ) : (
                <div className={styles.reviewValue}>
                  {draft.category ? localizedProjectCategory(draft.category as any) : '—'}
                  {draft.projectType ? ` › ${localizedProjectType(draft.projectType)}` : ''}
                  {draft.projectSubtype ? ` › ${localizedProjectSubtype(draft.projectSubtype)}` : ''}
                  {draft.projectStatus ? ` · ${localizedProjectStatus(draft.projectStatus as any)}` : ''}
                </div>
              )}
            </div>

            {/* Location */}
            <div className={styles.reviewSection}>
              <div className={styles.reviewLabel}>
                {t('stepper.locationTitle')}
                <button className={styles.reviewEditBtn} onClick={() => goToStep(2)} type="button">{t('stepper.edit')}</button>
              </div>
              {skippedSteps.has('location') ? (
                <div className={styles.reviewSkipped}>{t('stepper.skippedNote')}</div>
              ) : (
                <div className={styles.reviewValue}>
                  {draft.locationLat && draft.locationLon
                    ? `${draft.locationLat}, ${draft.locationLon}`
                    : '—'}
                </div>
              )}
            </div>

            {/* Community */}
            <div className={styles.reviewSection}>
              <div className={styles.reviewLabel}>
                {t('stepper.communityLabel')}
                <button className={styles.reviewEditBtn} onClick={() => goToStep(3)} type="button">{t('stepper.edit')}</button>
              </div>
              {skippedSteps.has('community') ? (
                <div className={styles.reviewSkipped}>{t('stepper.skippedNote')}</div>
              ) : (
                <div className={styles.reviewValue}>
                  {draft.communityDisplay || '—'}
                </div>
              )}
            </div>

            {/* Timeline */}
            <div className={styles.reviewSection}>
              <div className={styles.reviewLabel}>
                {t('stepper.timelineTitle')}
                <button className={styles.reviewEditBtn} onClick={() => goToStep(4)} type="button">{t('stepper.edit')}</button>
              </div>
              {skippedSteps.has('timeline') ? (
                <div className={styles.reviewSkipped}>{t('stepper.skippedNote')}</div>
              ) : (
                <div className={styles.reviewValue}>
                  {draft.startDate || draft.endDate
                    ? `${draft.startDate || '?'} → ${draft.endDate || '?'}`
                    : '—'}
                </div>
              )}
            </div>

            {error && (
              <div style={{ color: 'var(--accent-danger)', fontSize: 13, marginTop: 12 }}>{error}</div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  // ── Step metadata for header/hint ─────────────────────────
  const stepMeta: Record<string, { title: string; hint: string }> = {
    name:      { title: t('stepper.step1Title'), hint: t('stepper.step1Hint') },
    category:  { title: t('stepper.step2Title'), hint: t('stepper.step2Hint') },
    location:  { title: t('stepper.step3Title'), hint: t('stepper.step3Hint') },
    community: { title: t('stepper.step4Title'), hint: t('stepper.step4Hint') },
    timeline:  { title: t('stepper.step5Title'), hint: t('stepper.step5Hint') },
    kpi:       { title: t('stepper.step6Title'), hint: t('stepper.step6Hint') },
    preflight: { title: t('stepper.step7Title'), hint: t('stepper.step7Hint') },
    review:    { title: t('stepper.step8Title'), hint: t('stepper.step8Hint') },
  };

  const meta = currentStep ? stepMeta[currentStep.key] : null;
  const isLast = stepIndex === steps.length - 1;
  const isFirst = stepIndex === 0;

  return (
    <div className={styles.shell}>
      {/* ── Top bar ── */}
      <div className={styles.topBar}>
        <button
          className={styles.backBtn}
          onClick={() => {
            if (isFirst) setPath(null);
            else goBack();
          }}
          type="button"
        >
          ← {t('stepper.back')}
        </button>
        <div className={styles.topTitle}>{t('stepper.title')}</div>

        {/* Progress dots */}
        <div className={styles.progressBar}>
          {steps.map((s, i) => (
            <React.Fragment key={s.key}>
              {i > 0 && <div className={styles.progressLine} data-done={i <= stepIndex} />}
              <div
                className={styles.progressDot}
                data-state={
                  i < stepIndex
                    ? (skippedSteps.has(s.key) ? 'skipped' : 'completed')
                    : i === stepIndex
                      ? 'current'
                      : 'upcoming'
                }
                title={stepMeta[s.key]?.title}
              />
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── Step content ── */}
      <div className={styles.content}>
        <div className={styles.stepContainer}>
          {meta && (
            <>
              <div className={styles.stepLabel}>
                {t('stepper.stepN', { n: stepIndex + 1, total: steps.length })}
              </div>
              <h2 className={styles.stepTitle}>{meta.title}</h2>
              <p className={styles.stepHint}>{meta.hint}</p>
            </>
          )}
          {renderStep()}
        </div>
      </div>

      {/* ── Footer ── */}
      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          <button className={styles.btnSecondary} onClick={() => navigate('/projects')} type="button">
            {t('stepper.cancel')}
          </button>
          {!currentStep?.required && !isLast && (
            <button className={styles.btnSecondary} onClick={skipStep} type="button">
              {t('stepper.skip')}
            </button>
          )}
        </div>
        <div className={styles.footerRight}>
          {isLast ? (
            <button
              className={styles.btnPrimary}
              onClick={handleCreate}
              disabled={!canProceed || creating}
              type="button"
            >
              {creating ? t('stepper.creating') : t('stepper.createProject')}
            </button>
          ) : (
            <button
              className={styles.btnPrimary}
              onClick={goNext}
              disabled={!canProceed}
              type="button"
            >
              {t('stepper.next')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
